import { Injectable } from "@angular/core";
import { Title } from "@angular/platform-browser";
import { MetaService } from "@ngx-meta/core";
import { environment } from "../../../environments/environment";
import {
  DetailedProduct,
  DetailedSolution
} from "../../core/models/products.model";
import { SiilaTranslationService } from "../../core/services/siila-translation.service";
import { NewsDetail } from "../models/news.model";
import { clearPrerenderRedirect } from "../utils/prerender";
import { PrerenderReadyService } from "./prerender-ready.service";

export interface MetaTag {
  name: string;
  content: string;
}

@Injectable()
export class MetaTagService {
  RESOURCE_SOCIAL_SHARE_IMG =
    "/assets/icons/RESOURCE-SOCIAL-SHARE-SiiLA-May9-HR.png";

  /** Maps the app's internal language code to its hreflang attribute value. */
  private readonly HREFLANG_CODE: { [lang: string]: string } = {
    en: "en",
    pt: "pt-BR",
    es: "es"
  };

  constructor(
    private title: Title,
    private metaSrv: MetaService,
    private siilaTranslationService: SiilaTranslationService,
    private prerenderReadyService: PrerenderReadyService
  ) {}

  private setDynamicTags(
    tags: MetaTag[],
    title?: string,
    noUpperCase?: boolean
  ) {
    if (title && (!noUpperCase || noUpperCase == undefined)) {
      title = title.toUpperCase();
    }
    this.title.setTitle(title || this.title.getTitle());
    if (title) {
      this.metaSrv.setTitle(title);
    }

    tags.forEach(t => {
      if (t.name) {
        this.updateTag(t.name, t.content);
      }
    });
  }

  private updateTag(tagName: string, contentTag: string) {
    this.metaSrv.removeTag(`property='${tagName}'`);
    this.metaSrv.removeTag(`name='${tagName}'`);
    this.metaSrv.setTag(tagName, contentTag);
  }

  /**
   * Sets the `<meta name="prerender-status-code" content="<code>">` tag that
   * Prerender.io / search engines read to decide HTTP status.
   *
   * Clears the whole redirect pair first, not just the status tag: an earlier
   * in-app navigation may have emitted `301 + prerender-header: Location: ...`
   * for an off-canonical article URL, and that header survives a client-side
   * route change. Without the clear, a later 404/503 would be served to the
   * crawler alongside a stale `Location` pointing at an unrelated page.
   */
  setPrerenderStatusCode(code: number): void {
    clearPrerenderRedirect(document);
    this.metaSrv.setTag("prerender-status-code", String(code));
    this.prerenderReadyService.signalReady();
  }

  getMetaTagsFrom(
    title: string,
    description: string,
    keywords: string,
    imgPath?: string
  ): any[] {
    let baseImg = "/assets/icons/SOCIAL-SHARE-SiiLA.png";
    if (imgPath) {
      baseImg = imgPath;
    }

    const tags = [
      // general
      { name: "name", content: title },
      { name: "keywords", content: keywords },
      { name: "og:email", content: this.contactEmail() },
      {
        name: "lang",
        content: this.siilaTranslationService.getCurrentLanguage()
      },
      { name: "author", content: "SiiLA" },
      {
        name: "og:url",
        content: encodeURI(this.cleanLangFromUrl(window.location.href))
      },
      { name: "og:site_name", content: title },
      {
        name: "og:site_url",
        content: encodeURI(this.cleanLangFromUrl(window.location.href))
      },
      // Title
      { name: "og:title", content: title }, //don't prepend for og:title tag but do for generic and twitter
      { name: "twitter:title", content: title },
      {
        name: "mobile-web-app-title",
        content: encodeURIComponent(title)
      },
      // Description
      { name: "og:description", content: description },
      { name: "description", content: description },
      { name: "twitter:description", content: description },
      // Image
      {
        name: "og:image",
        property: "og:image",
        content: encodeURI(environment.applicationURL + baseImg)
      },
      {
        name: "image",
        content: encodeURI(environment.applicationURL + baseImg)
      },
      {
        name: "twitter:image",
        content: encodeURI(environment.applicationURL + baseImg)
      },
      {
        name: "og:image:width",
        content: 440
      },
      {
        name: "og:image:height",
        content: 220
      }
    ];

    //this.addCanonicalTag(`${encodeURI(window.location.href)}`);

    return tags;
  }

  /**
   * Collapse the trailing slash on the SITE ROOT only — `https://host/` becomes
   * `https://host`. Every URL that has a real path is returned untouched.
   *
   * The root is the one URL where this app's two URL builders disagreed. A
   * browser normalizes the root to `https://host/`, so `addCanonicalTag` — fed
   * `window.location.href` — emitted the slash; the hreflang set is built via
   * `SiilaTranslationService.applyLangSuffix`, which strips a trailing slash so
   * `/lang/<code>` attaches cleanly, so it emitted none. The homepage therefore
   * declared `https://host/` canonical while advertising `https://host` as its
   * own default-language and `x-default` alternate — the mismatch Ahrefs flagged.
   *
   * It also made the SAME page emit two different canonicals: arriving on
   * `/lang/<default>` left no slash behind once the suffix was stripped, while
   * arriving on `/` kept one.
   *
   * Deliberately anchored to `scheme://host` + only-slashes rather than a blanket
   * `/\/+$/`. A blanket strip would also rewrite `…/products/` and any other
   * trailing-slash URL a visitor happens to arrive on — URLs this app never
   * generates (the only trailing-slash link in the codebase is the root
   * `routerLink="/"`), so rewriting them would change canonical for pages the
   * ticket never covered, and would point them at a URL shape the server may not
   * serve. The anchoring also makes the helper inert for the relative paths
   * `UrlCanonicalService.apply` returns during SSR, and makes it impossible to
   * reduce a URL to the empty string (an empty `href` resolves to the current
   * page, which would silently make every URL self-canonical).
   */
  private stripRootTrailingSlash(url: string): string {
    return url.replace(/^(https?:\/\/[^/]+)\/+$/i, "$1");
  }

  /**
   * Strip a trailing `/lang/<code>` segment from a URL (preserving any query
   * string), but ONLY when it is THIS deployment's default-language suffix. This
   * mirrors `addCanonicalTag`: default-language variants collapse onto the clean
   * URL (killing "Duplicate without user-selected canonical"), while real
   * alternates (e.g. `/lang/en` on BR/MX/CO, `/lang/es` on LATAM) stay on their
   * self-canonical URL — so `og:url`/`og:site_url` match the canonical instead
   * of wrongly pointing every alternate page at the default-language URL. The
   * default code is platform-aware (BR->pt-br, MX/CO->es, LATAM->en).
   */
  private cleanLangFromUrl(url: string): string {
    if (url == null || url === undefined) {
      return url;
    }
    const [base, query] = url.split("?");
    const defaultCode = this.siilaTranslationService.getDefaultLangSuffixCode();
    const cleanBase = this.stripRootTrailingSlash(
      base.replace(new RegExp(`/lang/${defaultCode}/?$`, "i"), "")
    );
    return query ? `${cleanBase}?${query}` : cleanBase;
  }

  public addCanonicalTag(currentPath: string) {
    let path = currentPath;
    if (path == null || path === undefined) {
      return path;
    }

    // remove request params from path
    path = path.split("?")[0];

    // Strip the trailing /lang/<code> segment ONLY when it is THIS deployment's
    // default language, so default-language variants collapse onto the clean URL
    // (killing "Duplicate without user-selected canonical") while real alternates
    // (e.g. /lang/en on BR) stay self-canonical and are linked via hreflang. The
    // default code is platform-aware (BR->pt-br, MX/CO->es, LATAM->en).
    const defaultCode = this.siilaTranslationService.getDefaultLangSuffixCode();
    path = path.replace(new RegExp(`/lang/${defaultCode}/?$`, "i"), "");

    // Root only: match the hreflang set, which is built through `applyLangSuffix`
    // and never carries a trailing slash. See `stripRootTrailingSlash`.
    path = this.stripRootTrailingSlash(path);

    var existingCanonicalLink = document.querySelector(
      `link[rel='canonical']`
    ) as HTMLLinkElement;

    if (!existingCanonicalLink) {
      const linkTag = document.createElement("link");
      linkTag.setAttribute("rel", "canonical");
      linkTag.href = path;
      document.head.appendChild(linkTag);
    } else {
      existingCanonicalLink.href = path;
    }
  }

  /**
   * Emit `<link rel="alternate" hreflang="…">` tags for every language THIS
   * deployment supports, plus an `x-default` pointing at the default-language
   * (clean) URL. Derives the alternate set from the platform's supported
   * languages so it is correct on all four deployments (BR: pt-BR+en, MX/CO:
   * es+en, LATAM: en). All URLs are built through `buildCrossCountryUrl`, which
   * strips any incoming `/lang` suffix and re-applies the conditional one — so
   * passing the clean base OR a `/lang` variant yields the SAME set, satisfying
   * hreflang reciprocity on every URL variant. Pass the route path (not
   * `window.location.href`) to stay SSR-safe and avoid the replaceState mutation.
   */
  public addHreflangTags(
    basePath: string,
    localizePathForLang?: (lang: string) => string,
    originalRegionId?: number
  ) {
    if (basePath == null || basePath === undefined) {
      return;
    }

    try {
      // Remove any existing hreflang tags before re-emitting.
      document
        .querySelectorAll(`link[rel='alternate'][hreflang]`)
        .forEach(el => el.parentNode && el.parentNode.removeChild(el));

      const sts = this.siilaTranslationService;
      const platform = environment.platformId;
      const defaultLang = sts.getDefaultLanguage();

      // Default resolver preserves the original behavior (same path for every
      // language). Routes whose slug changes per language (news/noticias) pass a
      // resolver that rewrites the path prefix + translated title per language.
      const resolve = localizePathForLang || (() => basePath);

      let tags = sts.getSupportedLanguages().map(lang => ({
        hreflang: this.HREFLANG_CODE[lang] || lang,
        href: sts.buildCrossCountryUrl(platform, resolve(lang), lang)
      }));

      // On LATAM, a news item originally published for Brazil (region 101) has no
      // Spanish version — drop the `es` alternate so we don't advertise a
      // non-existent translation to search engines.
      const isBrazilOnLatam =
        platform === "latam" &&
        originalRegionId != null &&
        originalRegionId === 101;
      if (isBrazilOnLatam) {
        tags = tags.filter(t => t.hreflang !== "es");
      }

      // Stash the per-language LOCAL (current-deployment) address-bar paths so the
      // language switcher can re-slug the URL INSTANTLY on switch — no page-data
      // reload. Only for pages whose slug is language-dependent (a resolver was
      // passed); same-path pages keep the plain suffix behavior. Honors the
      // latam BR-news es-drop so we never register a non-existent translation.
      if (localizePathForLang) {
        const localMap: { [lang: string]: string } = {};
        sts.getSupportedLanguages().forEach(lang => {
          if (isBrazilOnLatam && lang === "es") {
            return;
          }
          localMap[lang] = sts.buildLocalizedPath("/" + resolve(lang), lang);
        });
        sts.setLocalizedPathMap(localMap);
      }

      tags.push({
        hreflang: "x-default",
        href: sts.buildCrossCountryUrl(
          platform,
          resolve(defaultLang),
          defaultLang
        )
      });

      tags.forEach(t => {
        if (!t.href) {
          return;
        }
        const link = document.createElement("link");
        link.setAttribute("rel", "alternate");
        link.setAttribute("hreflang", t.hreflang);
        link.setAttribute("href", encodeURI(t.href));
        document.head.appendChild(link);
      });
    } catch (e) {
      console.error("Error adding hreflang tags:", e);
    }
  }

  private contactEmail() {
    return environment.platformId === "mx" || environment.platformId == "co"
      ? "contacto@siila.com.mx"
      : environment.platformId === "br"
      ? "contato@siila.com.br"
      : "contact@siila.com";
  }

  public setHomePageMetaTags() {
    const title = this.siilaTranslationService.getTranslationFromJSONFiles(
      "metatags.home.title"
    );
    const tabTitle = this.siilaTranslationService.getTranslationFromJSONFiles(
      "metatags.home.tabTitle"
    );
    const content = this.siilaTranslationService.getTranslationFromJSONFiles(
      "metatags.home.subject"
    );
    const keywords = this.siilaTranslationService.getTranslationFromJSONFiles(
      "metatags.home.keywords"
    );
    let tags = this.getMetaTagsFrom(title, content, keywords);
    this.setDynamicTags(tags, tabTitle, true);
  }

  public setSpotPageMetaTags() {
    const title = this.siilaTranslationService.getTranslationFromJSONFiles(
      "metatags.spot.title"
    );
    const tabTitle = this.siilaTranslationService.getTranslationFromJSONFiles(
      "metatags.spot.tabTitle"
    );
    const content = this.siilaTranslationService.getTranslationFromJSONFiles(
      "metatags.spot.subject"
    );
    const keywords = this.siilaTranslationService.getTranslationFromJSONFiles(
      "metatags.spot.keywords"
    );
    let tags = this.getMetaTagsFrom(title, content, keywords);
    this.setDynamicTags(tags, tabTitle, true);
  }

  public setAllProductsMetaTags() {
    const rootFolder = "metatags.allProducts.";
    const title = this.siilaTranslationService.getTranslationFromJSONFiles(
      rootFolder + "title"
    );
    const tabTitle = this.siilaTranslationService.getTranslationFromJSONFiles(
      rootFolder + "tabTitle"
    );
    const content = this.siilaTranslationService.getTranslationFromJSONFiles(
      rootFolder + "subject"
    );
    const keywords = this.siilaTranslationService.getTranslationFromJSONFiles(
      rootFolder + "keywords"
    );
    let tags = this.getMetaTagsFrom(title, content, keywords);
    this.setDynamicTags(tags, tabTitle);
  }

  public setMarketAnalyticsMetaTags() {
    const rootFolder = "metatags.marketAnalytics.";
    const title = this.siilaTranslationService.getTranslationFromJSONFiles(
      rootFolder + "title"
    );
    const tabTitle = this.siilaTranslationService.getTranslationFromJSONFiles(
      rootFolder + "tabTitle"
    );
    const content = this.siilaTranslationService.getTranslationFromJSONFiles(
      rootFolder + "subject"
    );
    const keywords = this.siilaTranslationService.getTranslationFromJSONFiles(
      rootFolder + "keywords"
    );
    let tags = this.getMetaTagsFrom(title, content, keywords);
    this.setDynamicTags(tags, tabTitle);
  }

  public setAcademyMetaTags() {
    const rootFolder = "metatags.academy.";
    const title = this.siilaTranslationService.getTranslationFromJSONFiles(
      rootFolder + "title"
    );
    const tabTitle = this.siilaTranslationService.getTranslationFromJSONFiles(
      rootFolder + "tabTitle"
    );
    const content = this.siilaTranslationService.getTranslationFromJSONFiles(
      rootFolder + "subject"
    );
    const keywords = this.siilaTranslationService.getTranslationFromJSONFiles(
      rootFolder + "keywords"
    );
    let tags = this.getMetaTagsFrom(title, content, keywords);
    this.setDynamicTags(tags, tabTitle);
  }

  public setFibraAnalyticsMetaTags() {
    const platformId = environment.platformId;
    const rootFolder = "metatags.fibraanalytics.";
    const title = this.siilaTranslationService.getTranslationFromJSONFiles(
      rootFolder + "title" + platformId
    );
    const tabTitle = this.siilaTranslationService.getTranslationFromJSONFiles(
      rootFolder + "tabTitle" + platformId
    );
    const content = this.siilaTranslationService.getTranslationFromJSONFiles(
      rootFolder + "subject" + platformId
    );

    const keywords = this.siilaTranslationService.getTranslationFromJSONFiles(
      rootFolder + "keywords"
    );
    let tags = this.getMetaTagsFrom(title, content, keywords);
    this.setDynamicTags(tags, tabTitle);
  }

  public setAboutMetaTags() {
    const rootFolder = "metatags.about.";
    const title = this.siilaTranslationService.getTranslationFromJSONFiles(
      rootFolder + "title"
    );
    const tabTitle = this.siilaTranslationService.getTranslationFromJSONFiles(
      rootFolder + "tabTitle"
    );
    const content = this.siilaTranslationService.getTranslationFromJSONFiles(
      rootFolder + "subject"
    );

    const keywords = this.siilaTranslationService.getTranslationFromJSONFiles(
      rootFolder + "keywords"
    );
    let tags = this.getMetaTagsFrom(title, content, keywords);
    this.setDynamicTags(tags, tabTitle);
  }

  public setProductPageMetaTags(product: DetailedProduct) {
    if (product.id == 103 || product.id == 105) {
      this.setInvestorSurveyTags();
      return;
    }
    // Grocs BR
    if (product.id == 101) {
      this.setGrocsTags();
      return;
    }

    if (product.id == 102 || product.id == 104) {
      this.setIndicesTags();
      return;
    }

    const rootFolder = "metatags.solution.";
    const productName = this.siilaTranslationService.getTranslation(
      product.productShortName
    );
    const title = "SiiLA: " + productName;
    const tabTitle = productName;

    const content = this.siilaTranslationService.getTranslationFromJSONFiles(
      rootFolder + "subject"
    );
    const keywords = this.siilaTranslationService.getTranslationFromJSONFiles(
      rootFolder + "keywords"
    );
    let tags = this.getMetaTagsFrom(title, content, keywords);
    this.setDynamicTags(tags, tabTitle);
  }

  setIndicesTags() {
    const rootFolder = "metatags.indices.";
    const title = this.siilaTranslationService.getTranslationFromJSONFiles(
      rootFolder + "title"
    );
    const tabTitle = this.siilaTranslationService.getTranslationFromJSONFiles(
      rootFolder + "tabTitle"
    );
    const content = this.siilaTranslationService.getTranslationFromJSONFiles(
      rootFolder + "subject"
    );

    const keywords = this.siilaTranslationService.getTranslationFromJSONFiles(
      rootFolder + "keywords"
    );

    let tags = this.getMetaTagsFrom(title, content, keywords);
    this.setDynamicTags(tags, tabTitle);
  }

  setGrocsTags() {
    const rootFolder = "metatags.grocs.";
    const title = this.siilaTranslationService.getTranslationFromJSONFiles(
      rootFolder + "title"
    );
    const tabTitle = this.siilaTranslationService.getTranslationFromJSONFiles(
      rootFolder + "tabTitle"
    );
    const content = this.siilaTranslationService.getTranslationFromJSONFiles(
      rootFolder + "subject"
    );

    const keywords = this.siilaTranslationService.getTranslationFromJSONFiles(
      rootFolder + "keywords"
    );

    let tags = this.getMetaTagsFrom(title, content, keywords);
    this.setDynamicTags(tags, tabTitle);
  }

  setInvestorSurveyTags() {
    const rootFolder = "metatags.investorSurvey.";
    const title = this.siilaTranslationService.getTranslationFromJSONFiles(
      rootFolder + "title"
    );
    const tabTitle = this.siilaTranslationService.getTranslationFromJSONFiles(
      rootFolder + "tabTitle"
    );
    const content = this.siilaTranslationService.getTranslationFromJSONFiles(
      rootFolder + "subject"
    );

    const keywords = this.siilaTranslationService.getTranslationFromJSONFiles(
      rootFolder + "keywords"
    );

    let tags = this.getMetaTagsFrom(title, content, keywords);
    this.setDynamicTags(tags, tabTitle);
  }

  removeHTMLCode(htmlText: string) {
    var div = document.createElement("div");
    div.innerHTML = htmlText;
    var text = div.textContent || div.innerText || "";
    text = text ? text.replace(/[\n\r\v\f]/g, " ") : "";
    return text;
  }

  truncateText(text: string) {
    if (text.length <= 250) {
      return text;
    }
    const truncatedText = text.substring(0, 247);
    const lastSpaceIndex = truncatedText.lastIndexOf(" ");
    if (lastSpaceIndex !== -1) {
      return truncatedText.substring(0, lastSpaceIndex) + "...";
    }
    return truncatedText;
  }

  getCloudImgURL(item: string): string {
    const url = item.replace("***", "640w");
    return `${environment.cloudfrontURL}${url}`;
  }

  public setInsightsDetailTags(news: NewsDetail) {
    const tabTitle = `${this.siilaTranslationService.getTranslation(
      news.headline
    )}`;
    const title = tabTitle;
    let content = this.siilaTranslationService.getTranslation(
      news.primaryContent
    );
    if (content) {
      content = this.removeHTMLCode(content);
      content = this.truncateText(content);
    }

    let headerImgCaption;
    if (news.headerImageCaption) {
      headerImgCaption = this.siilaTranslationService.getTranslation(
        news.headerImageCaption
      );
    }

    const headerImg = this.getCloudImgURL(news.headerImage.fullPath);
    let tags = this.getMetaTagsFrom(title, content, null);
    if (tags) {
      for (let t of tags) {
        if (
          t.name === "og:image" ||
          t.name === "image" ||
          t.name === "twitter:image"
        ) {
          t.content = encodeURI(headerImg);
        }
      }
      if (tags && headerImgCaption) {
        tags.push({ name: "og:image:alt", content: headerImgCaption });
        tags.push({ name: "twitter:image:alt", content: headerImgCaption });
      }
    }
    this.setDynamicTags(tags, tabTitle, true);
  }

  public setSolutionstMetaTags(solution: DetailedSolution) {
    if (solution.id == 101 || solution.id == 108) {
      this.setInstitutionalInvestors();
      return;
    }

    if (solution.id == 102 || solution.id == 109) {
      this.setBrokerageTags();
      return;
    }

    if (solution.id == 103 || solution.id == 110) {
      this.setOwnersOperatorDeveloperTags();
      return;
    }

    if (solution.id == 104 || solution.id == 111) {
      this.setTenantsTags();
      this.setTenantsTags();
      return;
    }

    if (solution.id == 105 || solution.id == 112) {
      this.setValuationAdvisorTags();
      return;
    }

    if (solution.id == 106 || solution.id == 113) {
      this.setServiceProviderTags();
      return;
    }

    if (solution.id == 107 || solution.id == 114 || solution.id == 122) {
      this.setReitInvestorTags();
      return;
    }

    const solutionName = this.siilaTranslationService.getTranslation(
      solution.shortName
    );
    const rootFolder = "metatags.solution.";
    const title = "SiiLA: " + solutionName;
    const tabTitle = solutionName;

    const content = this.siilaTranslationService.getTranslationFromJSONFiles(
      rootFolder + "subject"
    );
    const keywords = this.siilaTranslationService.getTranslationFromJSONFiles(
      rootFolder + "keywords"
    );
    let tags = this.getMetaTagsFrom(title, content, keywords);
    this.setDynamicTags(tags, tabTitle);
  }

  setInstitutionalInvestors() {
    const rootFolder = "metatags.investors.";
    const tabTitle = this.siilaTranslationService.getTranslationFromJSONFiles(
      rootFolder + "tabTitle"
    );
    const title =
      this.siilaTranslationService.getTranslationFromJSONFiles(
        rootFolder + "title"
      ) + tabTitle;
    const content = this.siilaTranslationService.getTranslationFromJSONFiles(
      rootFolder + "subject"
    );

    const keywords = this.siilaTranslationService.getTranslationFromJSONFiles(
      rootFolder + "keywords"
    );

    let tags = this.getMetaTagsFrom(title, content, keywords);
    this.setDynamicTags(tags, tabTitle);
  }

  setOwnersOperatorDeveloperTags() {
    const rootFolder = "metatags.owneroperatordevelopers.";
    const tabTitle = this.siilaTranslationService.getTranslationFromJSONFiles(
      rootFolder + "tabTitle"
    );
    const title =
      this.siilaTranslationService.getTranslationFromJSONFiles(
        rootFolder + "title"
      ) + tabTitle;
    const content = this.siilaTranslationService.getTranslationFromJSONFiles(
      rootFolder + "subject"
    );

    const keywords = this.siilaTranslationService.getTranslationFromJSONFiles(
      rootFolder + "keywords"
    );

    let tags = this.getMetaTagsFrom(title, content, keywords);
    this.setDynamicTags(tags, tabTitle);
  }

  setTenantsTags() {
    const rootFolder = "metatags.tenants.";
    const tabTitle = this.siilaTranslationService.getTranslationFromJSONFiles(
      rootFolder + "tabTitle"
    );
    const title =
      this.siilaTranslationService.getTranslationFromJSONFiles(
        rootFolder + "title"
      ) + tabTitle;
    const content = this.siilaTranslationService.getTranslationFromJSONFiles(
      rootFolder + "subject"
    );

    const keywords = this.siilaTranslationService.getTranslationFromJSONFiles(
      rootFolder + "keywords"
    );

    let tags = this.getMetaTagsFrom(title, content, keywords);
    this.setDynamicTags(tags, tabTitle);
  }

  public setInsightsHomeTags() {
    const platformId = environment.platformId;
    const rootFolder = "metatags.insights.";
    const title = this.siilaTranslationService.getTranslationFromJSONFiles(
      rootFolder + "title"
    );
    const tabTitle = this.siilaTranslationService.getTranslationFromJSONFiles(
      rootFolder + "tabTitle"
    );
    const content = this.siilaTranslationService.getTranslationFromJSONFiles(
      rootFolder + "subject" + platformId
    );

    const keywords = this.siilaTranslationService.getTranslationFromJSONFiles(
      rootFolder + "keywords"
    );
    let tags = this.getMetaTagsFrom(
      title,
      content,
      keywords,
      this.RESOURCE_SOCIAL_SHARE_IMG
    );
    this.setDynamicTags(tags, tabTitle, true);
  }

  public setPressHomeTags() {
    const platformId = environment.platformId;
    const rootFolder = "metatags.press.";
    const title = this.siilaTranslationService.getTranslationFromJSONFiles(
      rootFolder + "title"
    );
    const tabTitle = this.siilaTranslationService.getTranslationFromJSONFiles(
      rootFolder + "tabTitle"
    );
    const content = this.siilaTranslationService.getTranslationFromJSONFiles(
      rootFolder + "subject"
    );

    const keywords = this.siilaTranslationService.getTranslationFromJSONFiles(
      rootFolder + "keywords"
    );
    let tags = this.getMetaTagsFrom(title, content, keywords);
    this.setDynamicTags(tags, tabTitle, true);
  }

  setBrokerageTags() {
    const rootFolder = "metatags.brokerage.";
    const tabTitle = this.siilaTranslationService.getTranslationFromJSONFiles(
      rootFolder + "tabTitle"
    );
    const title =
      this.siilaTranslationService.getTranslationFromJSONFiles(
        rootFolder + "title"
      ) + tabTitle;
    const content = this.siilaTranslationService.getTranslationFromJSONFiles(
      rootFolder + "subject"
    );

    const keywords = this.siilaTranslationService.getTranslationFromJSONFiles(
      rootFolder + "keywords"
    );

    let tags = this.getMetaTagsFrom(title, content, keywords);
    this.setDynamicTags(tags, tabTitle);
  }

  setValuationAdvisorTags() {
    const rootFolder = "metatags.valuationAdvisor.";
    this.setSolutionTags(rootFolder);
  }

  setServiceProviderTags() {
    const rootFolder = "metatags.serviceProvider.";
    this.setSolutionTags(rootFolder);
  }

  setReitInvestorTags() {
    const rootFolder = "metatags.reitInvestors.";
    this.setSolutionTags(rootFolder);
  }

  setSolutionTags(rootFolder: string) {
    const tabTitle = this.siilaTranslationService.getTranslationFromJSONFiles(
      rootFolder + "tabTitle"
    );
    const title =
      this.siilaTranslationService.getTranslationFromJSONFiles(
        rootFolder + "title"
      ) + tabTitle;
    const content = this.siilaTranslationService.getTranslationFromJSONFiles(
      rootFolder + "subject"
    );

    const keywords = this.siilaTranslationService.getTranslationFromJSONFiles(
      rootFolder + "keywords"
    );

    let tags = this.getMetaTagsFrom(title, content, keywords);
    this.setDynamicTags(tags, tabTitle);
  }
}
